import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useOrganization } from '../hooks/useOrganization'
import { notificationsApi } from '../services/api'
import {
  Button,
  ActivityIndicator,
  ChangeSet,
  Checkbox,
  ConfirmDialog,
  DataTable,
  Field,
  Input,
  ModalShell,
  PageHeader,
  PageLayout,
  Select,
  StatePanel,
  StatusBadge,
  SummaryStrip,
  Tabs,
} from '../components/common'
import { buildPayloadChangeItems } from '../utils/payloadReview'
import styles from './NotificationsPage.module.css'

const EVENT_LABELS = {
  'feed.post_published': 'Post published',
  'creator.followed': 'Creator followed',
  'copy.started': 'Copy started',
  'copied_bet.placed': 'Copied bet placed',
  'copied_bet.settled': 'Copied bet settled',
  'feed.session_started': 'Feed entered',
  'feed.session_ended': 'Feed exited',
}

const PROVIDER_LABELS = {
  xtremepush: 'Xtremepush',
}


const providerLabel = (provider) => PROVIDER_LABELS[provider] || provider

const eventLabel = (eventType) => EVENT_LABELS[eventType] || eventType

const integrationLabel = (integration) => integration.name

const EMPTY_DRAFT = {
  name: '',
  provider: 'xtremepush',
  status: 'shadow',
  enabled_event_types: [],
  credential_secret_name: '',
  region: 'us',
  authentication: 'app_token',
  identity_namespace: 'sportsbook_player_id',
  publication_audience: 'creator_followers',
  request_ceiling_per_second: 5,
  maximum_drain_minutes: 15,
}

const REGION_LABELS = {
  us: 'United States',
  eu: 'European Union',
}

const AUTHENTICATION_LABELS = {
  app_token: 'App token',
  oauth_client_credentials: 'OAuth client credentials',
}

const configuredValue = (value, { currentValue, missing, proposedValue, side }) => {
  if (missing || !value) return 'Not configured'
  if (side === 'proposed' && currentValue && currentValue !== proposedValue) return 'Updated reference'
  return 'Configured'
}

const NOTIFICATION_REVIEW_FIELDS = {
  name: { label: 'Integration name', impact: 'Changes the operator-facing integration identity' },
  provider: { label: 'Provider', format: (value, { missing }) => missing ? 'Not configured' : providerLabel(value), impact: 'Controls the outbound delivery adapter' },
  status: { label: 'Delivery mode', impact: 'Controls whether provider requests are sent' },
  enabled_event_types: { label: 'Events', format: (value, { missing }) => missing ? 'Not configured' : value?.length ? value.map(eventLabel).join(', ') : 'None', impact: 'Changes which canonical events may be delivered' },
  credential_secret_name: { label: 'Credential', format: configuredValue, impact: 'Controls provider authentication availability' },
  'config.region': { label: 'Region', format: (value, { missing }) => missing ? 'Not configured' : REGION_LABELS[value] || value, impact: 'Changes the provider data residency endpoint' },
  'config.authentication': { label: 'Authentication', format: (value, { missing }) => missing ? 'Not configured' : AUTHENTICATION_LABELS[value] || value, impact: 'Changes the provider authentication flow' },
  'config.identity_namespace': { label: 'Identity namespace', impact: 'Changes which encrypted feed identity is delivered' },
  'config.publication_audience': { label: 'Publication audience', impact: 'Changes the notification audience contract' },
  'config.request_ceiling_per_second': { label: 'Request ceiling', impact: 'Changes maximum provider request rate' },
  'config.maximum_drain_minutes': { label: 'Maximum drain time', impact: 'Changes how long each delivery cycle may run' },
}

function buildIntegrationInput(source) {
  return {
    name: source.name.trim(),
    provider: 'xtremepush',
    status: source.status,
    enabled_event_types: source.enabled_event_types,
    credential_secret_name: source.credential_secret_name.trim() || null,
    config: {
      region: source.region,
      authentication: source.authentication,
      identity_namespace: source.identity_namespace.trim(),
      publication_audience: 'creator_followers',
      request_ceiling_per_second: Number(source.request_ceiling_per_second),
      maximum_drain_minutes: Number(source.maximum_drain_minutes),
    },
  }
}

function integrationToInput(integration) {
  if (!integration) return null
  return {
    name: integration.name,
    provider: integration.provider,
    status: integration.status,
    enabled_event_types: integration.enabled_event_types || [],
    credential_secret_name: integration.credential_secret_name || null,
    config: {
      region: integration.config?.region || null,
      authentication: integration.config?.authentication || null,
      identity_namespace: integration.config?.identity_namespace || null,
      publication_audience: integration.config?.publication_audience || null,
      request_ceiling_per_second: integration.config?.request_ceiling_per_second ?? null,
      maximum_drain_minutes: integration.config?.maximum_drain_minutes ?? null,
    },
  }
}

function NotificationsPage() {
  const { currentOrg } = useOrganization()
  const [integrations, setIntegrations] = useState([])
  const [eventTypes, setEventTypes] = useState([])
  const [counts, setCounts] = useState([])
  const [deadDeliveries, setDeadDeliveries] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [activeTab, setActiveTab] = useState('integrations')
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingIntegration, setEditingIntegration] = useState(null)
  const [draft, setDraft] = useState(EMPTY_DRAFT)
  const [formError, setFormError] = useState('')
  const [saving, setSaving] = useState(false)
  const [disableTarget, setDisableTarget] = useState(null)
  const [retryingID, setRetryingID] = useState('')

  const loadNotifications = useCallback(async () => {
    if (!currentOrg?.slug) return
    setLoading(true)
    setLoadError('')
    try {
      const [integrationResult, statusResult] = await Promise.all([
        notificationsApi.listIntegrations(currentOrg.slug),
        notificationsApi.getStatus(currentOrg.slug),
      ])
      setIntegrations(integrationResult.integrations || [])
      setEventTypes(integrationResult.event_types || [])
      setCounts(statusResult.counts || [])
      setDeadDeliveries(statusResult.dead_deliveries || [])
    } catch (error) {
      setLoadError(error.response?.data?.error || 'Notifications could not be loaded.')
    } finally {
      setLoading(false)
    }
  }, [currentOrg?.slug])

  useEffect(() => {
    loadNotifications()
  }, [loadNotifications])

  const integrationsByID = Object.fromEntries(integrations.map((integration) => [integration.id, integration]))
  const integrationNames = Object.fromEntries(integrations.map((integration) => [integration.id, integrationLabel(integration)]))
  const integrationGuidance = 'Start in shadow mode, then activate after credential and schema validation.'
  const pendingCount = counts.filter((item) => item.status === 'pending' || item.status === 'processing')
    .reduce((total, item) => total + item.count, 0)
  const deliveredCount = counts.filter((item) => item.status === 'delivered')
    .reduce((total, item) => total + item.count, 0)
  const deadCount = counts.filter((item) => item.status === 'dead')
    .reduce((total, item) => total + item.count, 0)
  const countRows = counts.map((item) => ({
    ...item,
    row_id: `${item.integration_id}:${item.status}:${item.error_class || 'none'}`,
  }))
  const integrationProposal = buildIntegrationInput(draft)
  const integrationChanges = buildPayloadChangeItems({
    current: integrationToInput(editingIntegration),
    proposed: integrationProposal,
    fieldMetadata: NOTIFICATION_REVIEW_FIELDS,
    defaultImpact: 'Changes outbound notification delivery',
  })

  const openCreate = () => {
    setEditingIntegration(null)
    setDraft({ ...EMPTY_DRAFT })
    setFormError('')
    setEditorOpen(true)
  }

  const openEdit = (integration) => {
    setEditingIntegration(integration)
    setDraft({
      name: integration.name,
      provider: integration.provider,
      status: integration.status === 'disabled' ? 'shadow' : integration.status,
      enabled_event_types: integration.enabled_event_types || [],
      credential_secret_name: integration.credential_secret_name || '',
      region: integration.config.region,
      authentication: integration.config.authentication,
      identity_namespace: integration.config.identity_namespace,
      publication_audience: integration.config.publication_audience,
      request_ceiling_per_second: integration.config.request_ceiling_per_second,
      maximum_drain_minutes: integration.config.maximum_drain_minutes,
    })
    setFormError('')
    setEditorOpen(true)
  }

  const closeEditor = () => {
    setEditingIntegration(null)
    setDraft(EMPTY_DRAFT)
    setEditorOpen(false)
    setFormError('')
  }

  const saveIntegration = async () => {
    if (!draft.name.trim()) {
      setFormError('Enter an integration name.')
      return
    }
    if (draft.enabled_event_types.length === 0) {
      setFormError('Select at least one event.')
      return
    }
    if (draft.status === 'active' && !draft.credential_secret_name.trim()) {
      setFormError('Add a Secret Manager reference before activating delivery.')
      return
    }
    setSaving(true)
    setFormError('')
    const input = buildIntegrationInput(draft)
    try {
      if (editingIntegration?.id) {
        await notificationsApi.updateIntegration(currentOrg.slug, editingIntegration.id, input)
        toast.success('Notification integration updated')
      } else {
        await notificationsApi.createIntegration(currentOrg.slug, input)
        toast.success('Notification integration created')
      }
      closeEditor()
      await loadNotifications()
    } catch (error) {
      setFormError(error.response?.data?.error || 'The integration could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  const disableIntegration = async () => {
    if (!disableTarget) return
    try {
      await notificationsApi.disableIntegration(currentOrg.slug, disableTarget.id)
      toast.success('Notification integration disabled')
      setDisableTarget(null)
      await loadNotifications()
    } catch (error) {
      toast.error(error.response?.data?.error || 'The integration could not be disabled.')
    }
  }

  const retryDelivery = async (deliveryID) => {
    setRetryingID(deliveryID)
    try {
      await notificationsApi.retryDelivery(currentOrg.slug, deliveryID)
      toast.success('Delivery queued for retry')
      await loadNotifications()
    } catch (error) {
      toast.error(error.response?.data?.error || 'The delivery could not be retried.')
    } finally {
      setRetryingID('')
    }
  }

  const integrationColumns = [
    { key: 'name', label: 'Integration', sortable: true, sticky: 'start', priority: 'primary' },
    { key: 'provider', label: 'Provider', sortable: true, priority: 'primary', render: (integration) => providerLabel(integration.provider) },
    {
      key: 'status',
      label: 'Mode',
      sortable: true,
      priority: 'primary',
      render: (integration) => <StatusBadge auto size="small">{integration.status}</StatusBadge>,
    },
    {
      key: 'enabled_event_types',
      label: 'Events',
      numeric: true,
      sortable: true,
      priority: 'secondary',
      sortValue: (integration) => integration.enabled_event_types?.length || 0,
      render: (integration) => integration.enabled_event_types?.length || 0,
    },
    {
      key: 'credential_secret_name',
      label: 'Credential',
      sortable: true,
      priority: 'tertiary',
      render: (integration) => integration.credential_secret_name || 'Not configured',
    },
    {
      key: 'actions',
      label: 'Actions',
      sticky: 'end',
      priority: 'primary',
      render: (integration) => (
        <div className={styles.rowActions}>
          <Button variant="secondary" size="compact" onClick={() => openEdit(integration)}>Edit</Button>
          {integration.status !== 'disabled' ? (
            <Button variant="ghost" size="compact" onClick={() => setDisableTarget(integration)}>Disable</Button>
          ) : null}
        </div>
      ),
    },
  ]

  const countColumns = [
    { key: 'integration_id', label: 'Integration', sortable: true, sticky: 'start', priority: 'primary', sortValue: (item) => integrationNames[item.integration_id] || '', render: (item) => integrationNames[item.integration_id] || 'Removed integration' },
    { key: 'status', label: 'Status', sortable: true, priority: 'primary', render: (item) => <StatusBadge auto size="small">{item.status}</StatusBadge> },
    { key: 'error_class', label: 'Reason', sortable: true, priority: 'secondary', render: (item) => item.error_class || '—' },
    { key: 'count', label: 'Deliveries', numeric: true, sortable: true, priority: 'primary' },
  ]

  const deadColumns = [
    { key: 'event_type', label: 'Event', sortable: true, sticky: 'start', priority: 'primary', sortValue: (item) => eventLabel(item.event_type), render: (item) => eventLabel(item.event_type) },
    { key: 'integration_id', label: 'Integration', sortable: true, priority: 'primary', sortValue: (item) => integrationNames[item.integration_id] || '', render: (item) => integrationNames[item.integration_id] || 'Removed integration' },
    { key: 'error_class', label: 'Reason', sortable: true, priority: 'secondary', render: (item) => item.error_class || '—' },
    { key: 'attempt_count', label: 'Attempts', sortable: true, numeric: true, priority: 'primary' },
    { key: 'response_status', label: 'HTTP', sortable: true, numeric: true, priority: 'secondary', render: (item) => item.response_status || '—' },
    { key: 'updated_at', label: 'Last attempt', sortable: true, priority: 'secondary', render: (item) => new Date(item.updated_at).toLocaleString() },
    {
      key: 'retry',
      label: 'Action',
      sticky: 'end',
      priority: 'primary',
      render: (item) => {
        const integration = integrationsByID[item.integration_id]
        const canRetry = integration?.status === 'shadow' || integration?.status === 'active'
        return canRetry ? (
          <Button
            variant="secondary"
            size="compact"
            loading={retryingID === item.id}
            disabled={Boolean(retryingID)}
            onClick={() => retryDelivery(item.id)}
          >
            Retry
          </Button>
        ) : <span className={styles.managedAction}>Unavailable</span>
      },
    },
  ]

  if (loading) {
    return <PageLayout><StatePanel state="loading" title="Loading notifications" description="Checking integrations and delivery state." /></PageLayout>
  }
  if (loadError) {
    return <PageLayout><StatePanel state="error" title="Notifications unavailable" description={loadError} onRetry={loadNotifications} /></PageLayout>
  }

  return (
    <PageLayout size="wide">
      <PageHeader
        title="Notifications"
        subtitle="Manage outbound event delivery for this organization."
        primaryAction={<Button onClick={openCreate}>Add integration</Button>}
        secondaryActions={<Button variant="secondary" onClick={loadNotifications}>Refresh status</Button>}
      />

      <SummaryStrip
        label="Notification delivery summary"
        items={[
          { key: 'active', label: 'Active integrations', value: integrations.filter((item) => item.status === 'active').length },
          { key: 'queued', label: 'Queued', value: pendingCount },
          { key: 'delivered', label: 'Delivered', value: deliveredCount },
          { key: 'dead', label: 'Needs attention', value: deadCount },
        ]}
      />

      <Tabs
        label="Notification management"
        value={activeTab}
        onValueChange={setActiveTab}
        items={[
          {
            value: 'integrations',
            label: 'Integrations',
            count: integrations.length,
            panel: (
              <section className={styles.section} aria-labelledby="notification-integrations-heading">
                <div className={styles.sectionHeading}>
                  <div>
                    <h2 id="notification-integrations-heading">Outbound integrations</h2>
                    <p>{integrationGuidance}</p>
                  </div>
                </div>
                <DataTable
                  label="Outbound notification integrations"
                  data={integrations}
                  columns={integrationColumns}
                  keyField="id"
                  emptyMessage={<StatePanel compact title="No outbound integrations" description="Add an integration to begin validating event delivery." />}
                  responsiveColumns
                />
              </section>
            ),
          },
          {
            value: 'delivery',
            label: 'Delivery',
            count: deadCount || undefined,
            panel: (
              <div className={styles.deliveryStack}>
                <section className={styles.section} aria-labelledby="delivery-status-heading">
                  <div className={styles.sectionHeading}>
                    <div>
                      <h2 id="delivery-status-heading">Delivery status</h2>
                      <p>Counts are grouped by integration, state, and error class.</p>
                    </div>
                  </div>
                  <DataTable responsiveColumns label="Notification delivery counts" data={countRows} columns={countColumns} keyField="row_id" emptyMessage="No deliveries have been queued." />
                </section>
                <section className={styles.section} aria-labelledby="dead-deliveries-heading">
                  <div className={styles.sectionHeading}>
                    <div>
                      <h2 id="dead-deliveries-heading">Needs attention</h2>
                      <p>Only terminal delivery metadata is shown. Event payloads and provider response bodies are never exposed.</p>
                    </div>
                  </div>
                  {retryingID ? (
                    <ActivityIndicator status="retrying" label="Retrying notification delivery" detail="The delivery is being queued for another attempt." compact />
                  ) : null}
                  <DataTable responsiveColumns label="Dead notification deliveries" data={deadDeliveries} columns={deadColumns} keyField="id" emptyMessage="No dead deliveries." />
                </section>
              </div>
            ),
          },
        ]}
      />

      <ModalShell
        open={editorOpen}
        title={editingIntegration ? 'Edit notification integration' : 'Add notification integration'}
        description="Configure tenant-scoped delivery without storing provider credentials in Studio."
        size="large"
        onClose={closeEditor}
        footer={(
          <>
            <Button variant="secondary" onClick={closeEditor}>Cancel</Button>
            <Button loading={saving} onClick={saveIntegration}>{editingIntegration ? 'Save integration' : 'Add integration'}</Button>
          </>
        )}
      >
        <div className={styles.formStack}>
          {formError ? <StatePanel compact state="error" title="Integration not saved" description={formError} /> : null}
          <div className={styles.formGrid}>
            <Field label="Integration name" required>
              <Input value={draft.name} onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} placeholder="Desert Diamond Xtremepush" />
            </Field>
            <Field label="Delivery mode" help={draft.status === 'active' ? 'Active mode sends provider requests.' : 'Shadow mode validates events without sending provider requests.'} required>
              <Select value={draft.status} onChange={(event) => setDraft((current) => ({ ...current, status: event.target.value }))}>
                <option value="shadow">Shadow</option>
                <option value="active">Active</option>
              </Select>
            </Field>
            <Field label="Region" required>
              <Select value={draft.region} onChange={(event) => setDraft((current) => ({ ...current, region: event.target.value }))}>
                <option value="us">United States</option>
                <option value="eu">European Union</option>
              </Select>
            </Field>
            <Field label="Authentication" required>
              <Select value={draft.authentication} onChange={(event) => setDraft((current) => ({ ...current, authentication: event.target.value }))}>
                <option value="app_token">App token</option>
                <option value="oauth_client_credentials">OAuth client credentials</option>
              </Select>
            </Field>
            <Field label="Secret Manager reference" help="Enter the secret name only. Provider credentials remain in Google Secret Manager." optional={draft.status !== 'active'} required={draft.status === 'active'}>
              <Input value={draft.credential_secret_name} onChange={(event) => setDraft((current) => ({ ...current, credential_secret_name: event.target.value }))} placeholder="wager-feed-staging-xtremepush" autoComplete="off" />
            </Field>
            <Field label="Identity namespace" help="Must match the encrypted feed identity captured during identification." required>
              <Input value={draft.identity_namespace} onChange={(event) => setDraft((current) => ({ ...current, identity_namespace: event.target.value }))} />
            </Field>
            <Field label="Request ceiling" help="Requests per second, from 1 to 5." required>
              <Input type="number" min="1" max="5" value={draft.request_ceiling_per_second} onChange={(event) => setDraft((current) => ({ ...current, request_ceiling_per_second: event.target.value }))} />
            </Field>
            <Field label="Maximum drain time" help="Minutes before a delivery cycle yields, from 1 to 15." required>
              <Input type="number" min="1" max="15" value={draft.maximum_drain_minutes} onChange={(event) => setDraft((current) => ({ ...current, maximum_drain_minutes: event.target.value }))} />
            </Field>
          </div>
          <fieldset className={styles.eventFieldset}>
            <legend>Events</legend>
            <p>Select the canonical events this integration may receive.</p>
            <div className={styles.eventGrid}>
              {eventTypes.map((eventType) => (
                <Checkbox
                  key={eventType}
                  label={eventLabel(eventType)}
                  description={eventType}
                  checked={draft.enabled_event_types.includes(eventType)}
                  onChange={(event) => setDraft((current) => ({
                    ...current,
                    enabled_event_types: event.target.checked
                      ? [...current.enabled_event_types, eventType]
                      : current.enabled_event_types.filter((item) => item !== eventType),
                  }))}
                />
              ))}
            </div>
          </fieldset>
          {draft.name.trim() ? (
            <ChangeSet
              items={integrationChanges}
              title="Delivery changes"
              description="Review the fan-out and provider impact before saving."
            />
          ) : null}
        </div>
      </ModalShell>

      <ConfirmDialog
        open={Boolean(disableTarget)}
        title="Disable notification integration?"
        message="New events will stop being fanned out to this integration. Existing delivery records remain available for operations."
        confirmLabel="Disable integration"
        isDestructive
        onConfirm={disableIntegration}
        onCancel={() => setDisableTarget(null)}
      />
    </PageLayout>
  )
}

export default NotificationsPage
